import { useState } from "react";
import { api, TIMING_LABELS, type Task, type TaskInput, type User } from "../api";
import { goBack, pushScreen } from "../backstack";
import BackHeader from "../components/BackHeader";
import PinDialog from "../components/PinDialog";
import { UserField } from "../components/UserPicker";
import TaskForm, { type TaskSms } from "./TaskForm";
import { formatDue, isOverdue } from "./dates";
import { extractLinks } from "./links";
import { prepareImage } from "./photos";

const LABEL = "px-1 text-[12.5px] font-semibold text-[#3d2930]";

function ConfirmDialog({
  title,
  confirmLabel,
  onConfirm,
  onCancel,
}: {
  title: string;
  confirmLabel: string;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 px-6" onClick={onCancel}>
      <div
        className="pop-in flex w-full max-w-xs flex-col gap-4 rounded-[20px] bg-white p-6 shadow-pop"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-[19px] font-bold tracking-[-0.02em]">{title}</h2>
        <div className="flex gap-2">
          <button onClick={onCancel} className="h-12 flex-1 rounded-[14px] bg-white font-bold border border-line text-[#3a1c25]">
            Cancel
          </button>
          <button onClick={onConfirm} className="h-12 flex-1 rounded-[14px] bg-danger font-bold text-white">
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function TaskDetail({
  task,
  users,
  readOnly = false,
  onChange,
  onDeleted,
  onBack,
}: {
  task: Task;
  users: User[];
  /** The read-only PIN: no edit, done, owner, photo or delete controls. */
  readOnly?: boolean;
  onChange: (task: Task) => void;
  onDeleted: () => void;
  onBack: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [lightbox, setLightbox] = useState<number | null>(null);
  const [confirm, setConfirm] = useState<{ kind: "task" } | { kind: "photo"; id: number } | null>(null);

  const run = async (label: string, action: () => Promise<void>) => {
    if (busy) return;
    setBusy(label);
    setError(null);
    try {
      await action();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong");
    } finally {
      setBusy(null);
    }
  };

  const patch = (changes: Parameters<typeof api.updateTask>[1], label = "Saving…") =>
    run(label, async () => onChange(await api.updateTask(task.id, changes)));

  const addPhoto = (file: File | undefined) => {
    if (!file) return;
    run("Uploading photo…", async () => {
      const { id } = await api.uploadPhoto(task.id, await prepareImage(file));
      onChange({ ...task, photoIds: [...task.photoIds, id] });
    });
  };

  const openEditor = () => {
    setEditing(true);
    pushScreen(() => setEditing(false));
  };

  const openLightbox = (id: number) => {
    setLightbox(id);
    pushScreen(() => setLightbox(null));
  };

  const deletePhoto = (id: number) =>
    run("Deleting…", async () => {
      await api.deletePhoto(id);
      goBack(); // closes the lightbox via its history entry
      onChange({ ...task, photoIds: task.photoIds.filter((p) => p !== id) });
    });

  const deleteTask = async (pin: string) => {
    await api.deleteTask(task.id, pin);
    onDeleted();
  };

  if (editing) {
    return (
      <TaskForm
        title="Edit task"
        users={users}
        initial={task}
        onSave={async (input: TaskInput, _photo?: File, sms?: TaskSms) => {
          const updated = await api.updateTask(task.id, input);
          goBack();
          if (!sms) {
            onChange(updated);
            return;
          }
          try {
            await api.sendTaskSms(task.id, sms.to, sms.text);
            onChange({ ...updated, smsSent: true });
          } catch (err) {
            onChange(updated);
            setError(`Saved, but the SMS failed: ${err instanceof Error ? err.message : "unknown error"}`);
          }
        }}
        onCancel={goBack}
      />
    );
  }

  const overdue = !task.done && isOverdue(task.dueAt);
  const links = extractLinks(`${task.subject}\n${task.description}`);

  return (
    <div className="flex flex-1 flex-col gap-5">
      <BackHeader
        title="Task"
        onBack={onBack}
        action={
          !readOnly && (
            <button
              onClick={openEditor}
              className="shrink-0 flex h-10 items-center rounded-[12px] border border-[#efdce0] bg-white px-3.5 text-sm font-bold text-[#3a1c25] active:scale-95"
            >
              Edit
            </button>
          )
        }
      />

      <div className="pop-in flex flex-col gap-5">
        <div className="flex flex-col gap-2 rounded-[18px] bg-white p-5 border border-line-soft shadow-card">
          <div className={`text-sm font-bold ${overdue ? "text-danger" : "text-hot"}`}>
            {formatDue(task.dueAt)}
            {task.timing && ` · ${TIMING_LABELS[task.timing]}`}
            {overdue && " · overdue"}
          </div>
          <h2 className={`text-xl font-bold leading-snug ${task.done ? "text-ink/50 line-through" : ""}`}>
            {task.subject}
          </h2>
          {task.description ? (
            <p className="whitespace-pre-wrap text-sm font-semibold text-ink/70">{task.description}</p>
          ) : (
            <p className="text-sm font-semibold text-ink/40">No description.</p>
          )}
          {task.createdBy && <p className="text-xs font-semibold text-ink/50">From {task.createdBy}</p>}
          {task.done && task.doneAt && (
            <p className="text-xs font-semibold text-ink/50">Done {formatDue(task.doneAt)}</p>
          )}
        </div>

        {/* Links and phone numbers found in the text: open in the browser / call. */}
        {links.length > 0 && (
          <div className="flex flex-col gap-2">
            {links.map((l) =>
              l.kind === "url" ? (
                <a
                  key={l.href}
                  href={l.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex h-12 items-center justify-center gap-2 truncate rounded-[14px] bg-white px-5 text-sm font-bold active:scale-95 border border-line"
                >
                  <span aria-hidden>🌐</span> Open {l.label}
                </a>
              ) : (
                <a
                  key={l.href}
                  href={l.href}
                  className="flex h-12 items-center justify-center gap-2 rounded-[14px] bg-white px-5 text-sm font-bold active:scale-95 border border-line"
                >
                  <span aria-hidden>📞</span> Call {l.label}
                </a>
              ),
            )}
          </div>
        )}

        <div className="flex flex-col gap-1.5">
          <span className={LABEL}>Task owner</span>
          {readOnly ? (
            <div className="flex h-13 items-center rounded-[14px] bg-white px-3.5 font-semibold border border-line-soft shadow-card">
              {task.driverName ?? "Anyone"}
            </div>
          ) : (
            <UserField
              users={users}
              value={task.driverId}
              fallbackLabel={task.driverName}
              disabled={busy !== null}
              noneLabel="Anyone — tap to take this task"
              actionLabel="Change owner"
              onChange={(id) => patch({ driverId: id })}
            />
          )}
        </div>

        {!readOnly && (
          <button
            onClick={() => patch({ done: !task.done })}
            disabled={busy !== null}
            className={`h-16 w-full rounded-[14px] text-lg font-bold transition-transform active:scale-[0.98] disabled:opacity-60 ${
              task.done ? "border border-[#efdce0] bg-white text-[#3a1c25]" : "bg-rose-grad text-white shadow-button"
            }`}
          >
            {busy === "Saving…" ? "Saving…" : task.done ? "Mark as not done" : "✓ Mark as done"}
          </button>
        )}

        {(!readOnly || task.photoIds.length > 0) && (
        <section className="flex flex-col gap-2">
          <span className={LABEL}>Photos</span>
          {task.photoIds.length > 0 && (
            <div className="grid grid-cols-3 gap-2">
              {task.photoIds.map((id) => (
                <button key={id} onClick={() => openLightbox(id)} className="aspect-square overflow-hidden rounded-[14px] bg-white border border-line-soft shadow-card">
                  <img src={api.photoUrl(id)} alt="" loading="lazy" className="size-full object-cover" />
                </button>
              ))}
            </div>
          )}
          {!readOnly && (
          <div className="flex gap-2">
            <label className="flex h-12 flex-1 cursor-pointer items-center justify-center rounded-[14px] bg-blush text-sm font-bold text-rose-ink active:scale-95">
              📷 Take photo
              <input
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                disabled={busy !== null}
                onChange={(e) => {
                  addPhoto(e.target.files?.[0]);
                  e.target.value = "";
                }}
              />
            </label>
            <label className="flex h-12 flex-1 cursor-pointer items-center justify-center rounded-[14px] bg-white text-sm font-bold active:scale-95 border border-line">
              Add from library
              <input
                type="file"
                accept="image/*"
                className="hidden"
                disabled={busy !== null}
                onChange={(e) => {
                  addPhoto(e.target.files?.[0]);
                  e.target.value = "";
                }}
              />
            </label>
          </div>
          )}
          {busy === "Uploading photo…" && (
            <p className="text-center text-sm font-semibold text-ink/50">Uploading photo…</p>
          )}
        </section>
        )}

        {error && <p className="text-center text-sm font-semibold text-danger">{error}</p>}

        {!readOnly && (
          <button
            onClick={() => setConfirm({ kind: "task" })}
            disabled={busy !== null}
            className="mx-auto mt-2 px-4 py-2 text-sm font-bold text-danger"
          >
            Delete task
          </button>
        )}
      </div>

      {lightbox !== null && (
        <div className="fixed inset-0 z-50 flex flex-col bg-ink" onClick={goBack}>
          <img src={api.photoUrl(lightbox)} alt="" className="min-h-0 flex-1 object-contain" />
          <div className="flex gap-2 p-4" onClick={(e) => e.stopPropagation()}>
            <button onClick={goBack} className="h-12 flex-1 rounded-[14px] bg-white font-bold">
              Close
            </button>
            {!readOnly && (
              <button
                onClick={() => setConfirm({ kind: "photo", id: lightbox })}
                className="h-12 flex-1 rounded-[14px] bg-danger font-bold text-white"
              >
                Delete photo
              </button>
            )}
          </div>
        </div>
      )}

      {confirm?.kind === "task" && (
        <PinDialog
          title="Delete this task?"
          detail={`"${task.subject}" will be removed for everyone. Enter the PIN to delete.`}
          onConfirm={deleteTask}
          onCancel={() => setConfirm(null)}
        />
      )}
      {confirm?.kind === "photo" && (
        <ConfirmDialog
          title="Delete this photo?"
          confirmLabel="Delete"
          onConfirm={() => {
            const id = confirm.id;
            setConfirm(null);
            deletePhoto(id);
          }}
          onCancel={() => setConfirm(null)}
        />
      )}
    </div>
  );
}
